import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  FileText, 
  Search,
  Filter,
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
  Download,
  Loader2,
  Calendar,
  RefreshCw,
  Fingerprint,
  Copy
} from 'lucide-react';
import { format, parseISO, subDays } from 'date-fns';

const LogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [postedRecords, setPostedRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [jobFilter, setJobFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('7'); // days

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (jobFilter !== 'all') params.job_id = jobFilter;
      if (dateRange !== 'all') {
        params.start_date = subDays(new Date(), parseInt(dateRange)).toISOString();
      }
      params.limit = 200;

      const [logsRes, jobsRes] = await Promise.all([
        apiService.getAuditLogs(params),
        apiService.getJobs()
      ]);
      setLogs(logsRes.data);
      setJobs(jobsRes.data);
    } catch (error) {
      toast.error('Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, jobFilter, dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const viewLogDetail = async (log) => {
    setSelectedLog(log);
    setPostedRecords([]);
    setDetailOpen(true);
    // Posted-voucher GUID references are tracked per posting job.
    if (log.job_id && log.job_type !== 'fetch') {
      setRecordsLoading(true);
      try {
        const res = await apiService.getPostedRecords(log.job_id);
        setPostedRecords(res.data || []);
      } catch (error) {
        // Non-fatal: detail still shows without the references.
      } finally {
        setRecordsLoading(false);
      }
    }
  };

  const copyGuid = (guid) => {
    navigator.clipboard?.writeText(guid);
    toast.success('GUID copied');
  };

  const downloadXML = (content, filename) => {
    const blob = new Blob([content], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('XML downloaded');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return <Badge className="badge-success"><CheckCircle2 className="h-3 w-3 mr-1" />Success</Badge>;
      case 'failed':
        return <Badge className="badge-error"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
      case 'running':
        return <Badge className="badge-warning"><Clock className="h-3 w-3 mr-1" />Running</Badge>;
      default:
        return <Badge className="badge-neutral">{status}</Badge>;
    }
  };

  const filteredLogs = logs.filter(log => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        log.job_name?.toLowerCase().includes(query) ||
        log.action?.toLowerCase().includes(query) ||
        log.error_message?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="logs-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Audit Logs</h1>
          <p className="text-sm text-slate-500 mt-1">View all Tally posting activity and system logs</p>
        </div>
        <Button 
          variant="outline"
          onClick={loadData}
          data-testid="refresh-logs-btn"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <span className="text-sm font-medium text-slate-600">Filters:</span>
          </div>
          
          <div className="flex-1 relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9"
              data-testid="search-logs-input"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32 h-9" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="running">Running</SelectItem>
            </SelectContent>
          </Select>

          <Select value={jobFilter} onValueChange={setJobFilter}>
            <SelectTrigger className="w-40 h-9" data-testid="job-filter">
              <SelectValue placeholder="Job" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Jobs</SelectItem>
              {jobs.map((job) => (
                <SelectItem key={job.id} value={job.id}>{job.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-36 h-9" data-testid="date-filter">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 24 hours</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="table-header">
                <TableHead className="table-cell">Timestamp</TableHead>
                <TableHead className="table-cell">Job</TableHead>
                <TableHead className="table-cell">Action</TableHead>
                <TableHead className="table-cell">Status</TableHead>
                <TableHead className="table-cell">Records</TableHead>
                <TableHead className="table-cell">Error</TableHead>
                <TableHead className="table-cell text-right">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No logs found</p>
                    <p className="text-sm">Logs will appear here when jobs are executed</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredLogs.map((log) => (
                  <TableRow key={log.id} className="table-row" data-testid={`log-row-${log.id}`}>
                    <TableCell className="table-cell text-sm">
                      {format(parseISO(log.timestamp), 'MMM d, yyyy HH:mm:ss')}
                    </TableCell>
                    <TableCell className="table-cell font-medium">
                      {log.job_name || '-'}
                    </TableCell>
                    <TableCell className="table-cell">
                      <Badge className="badge-neutral">{log.action}</Badge>
                    </TableCell>
                    <TableCell className="table-cell">
                      {getStatusBadge(log.status)}
                    </TableCell>
                    <TableCell className="table-cell">
                      {log.records_processed > 0 ? (
                        <span className="text-sm">
                          <span className="text-emerald-600">{log.records_success}</span>
                          {' / '}
                          <span className={log.records_failed > 0 ? 'text-red-600' : ''}>{log.records_processed}</span>
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="table-cell">
                      {log.error_message ? (
                        <span className="text-xs text-red-600 truncate max-w-xs block">
                          {log.error_message}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="table-cell text-right">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => viewLogDetail(log)}
                        data-testid={`view-log-${log.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Stats Bar */}
      <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
        <span>Showing {filteredLogs.length} of {logs.length} logs</span>
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-full bg-emerald-500" />
            {logs.filter(l => l.status === 'success').length} success
          </span>
          <span className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-full bg-red-500" />
            {logs.filter(l => l.status === 'failed').length} failed
          </span>
          <span className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-full bg-amber-500" />
            {logs.filter(l => l.status === 'running').length} running
          </span>
        </div>
      </div>

      {/* Log Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Log Details</DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <ScrollArea className="flex-1 pr-4">
              <div className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Job Name</p>
                    <p className="font-medium">{selectedLog.job_name || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Action</p>
                    <Badge className="badge-neutral">{selectedLog.action}</Badge>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Timestamp</p>
                    <p className="font-medium">
                      {format(parseISO(selectedLog.timestamp), 'MMMM d, yyyy HH:mm:ss')}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Status</p>
                    {getStatusBadge(selectedLog.status)}
                  </div>
                </div>

                {/* Records */}
                {selectedLog.records_processed > 0 && (
                  <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-900">{selectedLog.records_processed}</p>
                      <p className="text-xs text-slate-500">Processed</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-emerald-600">{selectedLog.records_success}</p>
                      <p className="text-xs text-slate-500">Success</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-600">{selectedLog.records_failed}</p>
                      <p className="text-xs text-slate-500">Failed</p>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {selectedLog.error_message && (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500 font-medium">Error Message</p>
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                      {selectedLog.error_message}
                    </div>
                  </div>
                )}

                {/* Request XML */}
                {selectedLog.request_xml && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-slate-500 font-medium">Request XML</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadXML(selectedLog.request_xml, `request_${selectedLog.id}.xml`)}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                    </div>
                    <pre className="p-3 bg-slate-900 text-slate-100 rounded-lg text-xs overflow-auto max-h-40 font-mono">
                      {selectedLog.request_xml}
                    </pre>
                  </div>
                )}

                {/* Response XML */}
                {selectedLog.response_xml && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-slate-500 font-medium">Response XML</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadXML(selectedLog.response_xml, `response_${selectedLog.id}.xml`)}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                    </div>
                    <pre className="p-3 bg-slate-900 text-slate-100 rounded-lg text-xs overflow-auto max-h-40 font-mono">
                      {selectedLog.response_xml}
                    </pre>
                  </div>
                )}

                {/* Posted Voucher GUID References */}
                {selectedLog.job_type !== 'fetch' && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-slate-500 font-medium flex items-center gap-1.5">
                        <Fingerprint className="h-3.5 w-3.5 text-violet-600" />
                        Posted Voucher References
                        {postedRecords.length > 0 && (
                          <span className="text-slate-400">({postedRecords.length})</span>
                        )}
                      </p>
                      {recordsLoading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                    </div>
                    <p className="text-[11px] text-slate-400 -mt-1">
                      Current GUID and last action per voucher for this job — used to alter / repost.
                    </p>
                    {postedRecords.length === 0 ? (
                      <p className="text-xs text-slate-400 py-2">
                        {recordsLoading ? 'Loading references…' : 'No posted voucher references for this job yet.'}
                      </p>
                    ) : (
                      <div className="border border-slate-200 rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="table-header">
                              <TableHead className="table-cell">GUID</TableHead>
                              <TableHead className="table-cell">Last Action</TableHead>
                              <TableHead className="table-cell">Status</TableHead>
                              <TableHead className="table-cell">Posted At</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {postedRecords.map((rec, idx) => (
                              <TableRow key={rec.guid || rec.voucher_key || idx} className="table-row">
                                <TableCell className="table-cell font-mono text-xs">
                                  {rec.guid ? (
                                    <button
                                      onClick={() => copyGuid(rec.guid)}
                                      className="inline-flex items-center gap-1.5 hover:text-teal-700"
                                      title="Copy GUID"
                                    >
                                      <span className="truncate max-w-[280px]">{rec.guid}</span>
                                      <Copy className="h-3 w-3 opacity-50 shrink-0" />
                                    </button>
                                  ) : (
                                    <span className="text-slate-400">—</span>
                                  )}
                                </TableCell>
                                <TableCell className="table-cell">
                                  {rec.last_action ? (
                                    <Badge className={rec.last_action === 'alter' ? 'badge-warning' : 'badge-neutral'}>
                                      {rec.last_action}
                                    </Badge>
                                  ) : '—'}
                                </TableCell>
                                <TableCell className="table-cell">
                                  {rec.status ? getStatusBadge(rec.status) : '—'}
                                </TableCell>
                                <TableCell className="table-cell text-xs text-slate-500">
                                  {rec.posted_at ? format(parseISO(rec.posted_at), 'MMM d, HH:mm:ss') : '—'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LogsPage;
